import { postWithToken, getWithToken, putWithToken, patchWithToken } from "api/network";

const prospectsBaseUrl = "/api/explore/prospect";

export async function pushProspect(
    prospect,
    notes = '',
    price = null,
    inspectionPeriod = false,
    strategy,
    beds,
    baths,
    yearBuilt,
    sqft,
    prospectType,
    createdBy,
    opportunitySource
) {
    prospect.notes = notes;
    prospect.listingPrice = price;
    prospect.inspectionPeriod = inspectionPeriod;
    prospect.strategy = strategy || 'SFR';  // Default to SFR
    prospect.beds = beds > 0 ? beds : null;
    prospect.baths = baths > 0 ? baths : null;
    prospect.yearBuilt = yearBuilt;
    prospect.sqft = sqft;
    prospect.prospectType = prospectType || 0; //Default to 0 (Prospector)
    prospect.createdBy = createdBy;
    prospect.opportunitySource = opportunitySource;
    try {
        const response = await postWithToken(prospectsBaseUrl, prospect);
        if (response.status === 400) {
            const body = await response.json();
            return { success: 'error', message: body.message };
        } else if (response.status === 401 || response.status === 403) {
            return {
                success: 'error',
                message:
                    'Not Authorized. If you are, try reloading the application.'
            };
        }
        return { success: 'success', message: 'Property submitted successfully' };
    } catch (err) {
        return { success: 'error', message: err.message };
    }
}
export async function updateProspect(data, userId) {
    try {
        const prospect = {
            prospectId: data.id,
            beds: data.beds,
            baths: data.baths,
            sqft: data.sqft,
            yearBuilt: data.yearBuilt,
            inspectionPeriod: data.inspectionPeriod,
            strategy: data.strategy,
            listingPrice: data.listingPrice,
            notes: data.notes,
            updatedBy: userId,
            streetAddress: data.streetAddress,
            city: data.city,
            state: data.state,
            zipCode: data.zipCode,
            latitude: data.latitude,
            longitude: data.longitude,
            locationVerified: data.locationVerified,
            asgPropID: data.asgPropID,
            opportunitySource: data.opportunitySource
        }
        const response = await putWithToken(`${prospectsBaseUrl}/update`, prospect);
        if (response.status === 400) {
            const body = await response.json();
            return { success: 'error', message: body.message };
        } else if (response.status === 401 || response.status === 403) {
            return {
                success: 'error',
                message:
                    'Not Authorized. If you are, try reloading the application.'
            };
        }
        return { success: 'success', message: 'Property updated successfully' };
    } catch (err) {
        return { success: 'error', message: 'Failed to update property' };
    }
}
export async function rejectProspects(prospects) {
    const response = await patchWithToken(
        `${prospectsBaseUrl}/reject`, prospects
    );
    if (response.status === 400) {
        const body = await response.json();
        return { success: 'error', message: body.message };
    } else if (response.status === 401 || response.status === 403) {
        return {
            success: 'error',
            message:
                'Not Authorized. If you are, try reloading the application.'
        };
    }
    return { success: 'success', message: `${prospects.length} propert${prospects.length === 1 ? 'y' : 'ies'} rejected successfully` };
}
export async function createUnderwrite(prospects, user, importName, importOpportunityStatus) {
    let underwriteDate = new Date();
    underwriteDate.setDate(underwriteDate.getDate() + 35);  // This is just a placeholder value since CCD is required for import, backend will override this value during actual import.
    const importRows = prospects.map(prospect => {
        return {
            sourceId: prospect.id,
            street: prospect.streetAddress,
            city: prospect.city,
            state: prospect.state,
            zipCode: prospect.zipCode,
            yearBuilt: prospect.yearBuilt,
            sqft: prospect.sqft,
            beds: prospect.beds,
            baths: prospect.baths,
            county: prospect.county,
            opportunitySource: prospect.opportunitySource === '' ? null : prospect.opportunitySource,
            propertyType: 'Single Family',
            contractCloseDate: underwriteDate,
            latitude: prospect.latitude,
            longitude: prospect.longitude,
            prospectId: prospect.id,
            askingPrice: prospect.listingPrice,
            notes: prospect.notes,
            referenceId: prospect.id,
            totalUnits: 1               // Need for validation 
        };
    });
    const importPayload = {
        source: 'Prospects',
        channel: 4, // Off Market
        importCount: importRows.length,
        sender: 'ReVUe',
        importedBy: user.fullName,
        name: importName,
        requestedOpportunityStatus: importOpportunityStatus,
        importProperties: importRows   // create reference because we are going to clear out the id's for entityframework to set
    };
    try {
        const url = '/api/explore/prospect/createUnderwrite';
        const response = await postWithToken(url, importPayload);
        const validationResult = await response.json();
        if (response.status === 400) {
            const body = await response.json();
            return { success: 'error', message: body.message };
        } else if (response.status === 401 || response.status === 403) {
            return {
                success: 'error',
                message:
                    'Not Authorized. If you are, try reloading the application.'
            };
        }
        if (validationResult.passedValidation) {
            return { success: 'success', message: 'Property submitted successfully' };
        } else {
            let firstValidationError = validationResult.importItemValidationResults[0] && validationResult.importItemValidationResults[0].validationErrors[0];
            return { success: 'error', message: validationResult.errorMessage || (validationResult.dupCheckErrors[0] && validationResult.dupCheckErrors[0].errorMessage) || (firstValidationError && firstValidationError.errorMessage) };
        }
    } catch (err) {
        return { success: 'error', message: err.message };
    }
}
export async function getProspectGroupsPage(groupBy) {
    const groups = await getWithToken(
        `${prospectsBaseUrl}/Groups?group=${groupBy}`
    );
    return await groups.json();
}

export async function searchProspects(searchTerm, count) {
    const results = await getWithToken(
        `${prospectsBaseUrl}/search?searchTerm=${searchTerm}&count=${count}`
    );
    return await results.json();
}

export async function getProspects() {
    const results = await getWithToken(
        `${prospectsBaseUrl}`
    );
    return await results.json();
}

export async function getProspectsByTxnOppIds(txnOppIds) {
    const url = `${prospectsBaseUrl}/byTxnOppIds`;
    const response = await postWithToken(url, txnOppIds);
    const data = await response.json();
    return data;
}